import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Wallet, FileBarChart, Upload, Archive, Coins, MapPin, Plus, Pencil, Trash2, Download, X, FileText } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { MultiSelect } from '@/components/ui/MultiSelect';
import { SortableHeader } from '@/components/ui/SortableHeader';
import {
  FilterCheckbox,
  FilterPanel,
  FilterToggleButton,
  PageActions,
  PageTitle,
  PageToolbar,
  StatStrip,
  useFilterPanel,
} from '@/components/ui/PageToolbar';
import { TablePagination } from '@/components/ui/TablePagination';
import { useAuthStore } from '@/stores/sessionStore';
import { useUiStore } from '@/stores/uiStore';
import { apiError } from '@/lib/api-client';
import { safeUrl } from '@/lib/safeUrl';
import { useWorks, useWorkFacets, useArchiveWorkYear, useDeleteWork } from '@/features/works/useWorks';
import { WorkFormDialog } from '@/features/works/WorkFormDialog';
import { env } from '@/lib/env';
import type { WorkSortField, WorkRecordDto } from '@forest/contracts';

const lei = new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 0 });

function csv(values: string[]): string | undefined {
  return values.length > 0 ? values.join(',') : undefined;
}

export default function WorksPage() {
  const { t } = useTranslation(['works', 'common']);
  const user = useAuthStore((s) => s.user);
  const pushToast = useUiStore((s) => s.pushToast);
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  const [search, setSearch] = useState('');
  const [entitati, setEntitati] = useState<string[]>([]);
  const [years, setYears] = useState<string[]>([]);
  const [workTypes, setWorkTypes] = useState<string[]>([]);
  const [ocoale, setOcoale] = useState<string[]>([]);
  const [planFapt, setPlanFapt] = useState<'' | 'PLAN' | 'FAPT'>('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [sort, setSort] = useState<WorkSortField>('year');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [archiveYear, setArchiveYear] = useState<string>('');
  /** Filters collapse at every breakpoint so the table keeps the height.
   *  Remembered per browser. */
  const { open: filtersOpen, toggle: toggleFilters } = useFilterPanel('forest.ui.works.filters');
  const activeFilterCount =
    (entitati.length > 0 ? 1 : 0) +
    (ocoale.length > 0 ? 1 : 0) +
    (years.length > 0 ? 1 : 0) +
    (workTypes.length > 0 ? 1 : 0) +
    (planFapt ? 1 : 0) +
    (includeArchived ? 1 : 0);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<WorkRecordDto | null>(null);
  const deleteMut = useDeleteWork();

  const [sp, setSp] = useSearchParams();
  const parcelId = sp.get('parcelId') || undefined;

  const facets = useWorkFacets();
  const params = useMemo(
    () => ({
      page,
      limit,
      search: search || undefined,
      entitatiSilvice: csv(entitati),
      ocoaleSilvice: csv(ocoale),
      years: csv(years),
      workTypes: csv(workTypes),
      planFapt: planFapt || undefined,
      parcelId,
      includeArchived,
      sort,
      order,
    }),
    [page, limit, search, entitati, ocoale, years, workTypes, planFapt, parcelId, includeArchived, sort, order],
  );
  const query = useWorks(params);
  const archiveMut = useArchiveWorkYear();

  const reset = () => {
    setSearch('');
    setEntitati([]);
    setYears([]);
    setWorkTypes([]);
    setOcoale([]);
    setPlanFapt('');
    setIncludeArchived(false);
    setPage(1);
  };

  const onSort = (next: { sort: WorkSortField; order: 'asc' | 'desc' }) => {
    setPage(1);
    setSort(next.sort);
    setOrder(next.order);
  };

  const exportList = () => {
    const sp = new URLSearchParams();
    if (search) sp.set('search', search);
    if (entitati.length) sp.set('entitatiSilvice', entitati.join(','));
    if (ocoale.length) sp.set('ocoaleSilvice', ocoale.join(','));
    if (years.length) sp.set('years', years.join(','));
    if (workTypes.length) sp.set('workTypes', workTypes.join(','));
    if (planFapt) sp.set('planFapt', planFapt);
    if (includeArchived) sp.set('includeArchived', 'true');
    const a = document.createElement('a');
    a.href = `${env.VITE_API_URL}/api/works/export?${sp.toString()}`;
    a.rel = 'noopener';
    a.click();
  };

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (w: WorkRecordDto) => { setEditing(w); setFormOpen(true); };
  const doDelete = async (w: WorkRecordDto) => {
    if (!window.confirm(t('works:rowActions.deleteConfirm'))) return;
    try {
      await deleteMut.mutateAsync(w.id);
      pushToast({ variant: 'success', message: t('common:state.saved', { defaultValue: 'Șters' }) });
    } catch (e) {
      const err = await apiError(e);
      pushToast({ variant: 'error', message: err.message });
    }
  };

  const doArchive = async () => {
    const y = Number(archiveYear);
    if (!y) return;
    if (!window.confirm(t('works:archive.confirmBody', { year: y }))) return;
    try {
      const r = await archiveMut.mutateAsync(y);
      pushToast({ variant: 'success', message: t('works:archive.done', { count: r.archived, year: y }) });
    } catch (e) {
      const err = await apiError(e);
      pushToast({ variant: 'error', message: err.message });
    }
  };

  const facetYears = (facets.data?.years ?? []).map((y) => ({ value: String(y), label: String(y) }));
  const facetEntities = (facets.data?.entitatiSilvice ?? []).map((v) => ({ value: v, label: v }));
  const facetOcoale = (facets.data?.ocoaleSilvice ?? []).map((v) => ({ value: v, label: v }));
  const facetWorkTypes = (facets.data?.workTypes ?? []).map((v) => ({ value: v, label: v }));

  const items = query.data?.items ?? [];
  const totals = query.data?.totals;
  const pagination = query.data?.pagination;

  return (
    <div className="animate-fade-in flex h-full min-h-0 flex-col gap-2">
      {/* Row 1: title + inline stats + actions. */}
      <PageToolbar>
        <PageTitle title={t('works:page.title')} hint={t('works:page.subtitle')} />
        <StatStrip
          items={[
            { label: t('works:stats.records'), value: lei.format(totals?.count ?? 0), icon: Wallet, color: 'emerald' },
            { label: t('works:stats.totalCost'), value: lei.format(totals?.totalFinal ?? 0), icon: Coins, color: 'amber' },
            { label: t('works:stats.wages'), value: lei.format(totals?.wages ?? 0), icon: Coins, color: 'sky' },
            { label: t('works:stats.parcels'), value: lei.format(totals?.distinctParcels ?? 0), icon: MapPin, color: 'violet' },
          ]}
        />
        <PageActions>
          <Button size="sm" onClick={openNew}>
            <Plus className="h-3.5 w-3.5" /> {t('works:rowActions.new')}
          </Button>
          <Button size="sm" variant="secondary" onClick={exportList}>
            <Download className="h-3.5 w-3.5" /> XLSX
          </Button>
          {/* Styled links, not <Button> inside <Link>: a button nested in an
              anchor is invalid markup and gives two tab stops per action. */}
          <Link
            to="/app/works/reports"
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            <FileBarChart className="h-3.5 w-3.5" /> {t('works:actions.reports')}
          </Link>
          <Link
            to="/app/import?target=work"
            className="inline-flex items-center justify-center gap-1.5 rounded-md bg-forest-600 px-2.5 py-1 text-xs font-medium text-white transition-colors hover:bg-forest-700"
          >
            <Upload className="h-3.5 w-3.5" /> {t('works:actions.import')}
          </Link>
        </PageActions>
      </PageToolbar>

      {parcelId && (
        <div className="flex items-center gap-2 rounded border border-forest-200 bg-forest-50 px-2 py-1 text-xs text-forest-800 dark:border-forest-800 dark:bg-forest-900/20 dark:text-forest-200">
          <MapPin className="h-3.5 w-3.5" />
          <span>{t('works:filters.forParcel')}</span>
          <button
            onClick={() => setSp({})}
            aria-label={t('common:action.close')}
            className="ml-1 rounded p-0.5 hover:bg-forest-100 dark:hover:bg-forest-800"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Row 2: search + filter toggle. The grid below is collapsed by
          default at every breakpoint so the table keeps the height. */}
      <div className="flex items-center gap-2">
        <Input
          uiSize="sm"
          className="flex-1"
          value={search}
          onChange={(e) => { setPage(1); setSearch(e.target.value); }}
          placeholder={t('works:filters.search')}
        />
        <FilterToggleButton
          open={filtersOpen}
          count={activeFilterCount}
          onClick={toggleFilters}
          label={t('common:action.filters')}
        />
      </div>

      {filtersOpen && (
        <FilterPanel className="xl:grid-cols-7">
          {/* Placeholders carry the field name — with the labels gone,
              five dropdowns all reading "Toate" would be unusable. */}
          <MultiSelect uiSize="sm" options={facetEntities} value={entitati} onChange={(v) => { setPage(1); setEntitati(v); }} placeholder={t('works:filters.entity')} ariaLabel={t('works:filters.entity')} clearLabel={t('works:filters.reset')} />
          <MultiSelect uiSize="sm" options={facetOcoale} value={ocoale} onChange={(v) => { setPage(1); setOcoale(v); }} placeholder={t('works:filters.ocol')} ariaLabel={t('works:filters.ocol')} clearLabel={t('works:filters.reset')} />
          <MultiSelect uiSize="sm" options={facetYears} value={years} onChange={(v) => { setPage(1); setYears(v); }} placeholder={t('works:filters.year')} ariaLabel={t('works:filters.year')} clearLabel={t('works:filters.reset')} />
          <MultiSelect uiSize="sm" options={facetWorkTypes} value={workTypes} onChange={(v) => { setPage(1); setWorkTypes(v); }} placeholder={t('works:filters.workType')} ariaLabel={t('works:filters.workType')} clearLabel={t('works:filters.reset')} />
          <Select
            uiSize="sm"
            aria-label={t('works:filters.planFapt')}
            value={planFapt}
            onChange={(e) => { setPage(1); setPlanFapt(e.target.value as '' | 'PLAN' | 'FAPT'); }}
          >
            <option value="">{t('works:filters.planFapt')}</option>
            <option value="PLAN">{t('works:planFapt.PLAN')}</option>
            <option value="FAPT">{t('works:planFapt.FAPT')}</option>
          </Select>
          <FilterCheckbox
            checked={includeArchived}
            onChange={(next) => { setPage(1); setIncludeArchived(next); }}
            label={t('works:filters.archived')}
          />
          <Button size="sm" variant="ghost" className="h-8 !justify-start" onClick={reset}>
            {t('works:filters.reset')}
          </Button>
          {/* Year archiving (SUPER_ADMIN) rides along at the bottom of the
              panel instead of owning a card of its own. */}
          {isSuperAdmin && (
            <div className="col-span-full flex flex-wrap items-center gap-2 border-t border-slate-200 pt-1.5 dark:border-slate-700">
              <Archive className="h-3.5 w-3.5 flex-shrink-0 text-slate-500" />
              {/* Width lives on a wrapper: `Select` carries `w-full` in its
                  base classes and `cn()` has no tailwind-merge, so a
                  `w-32` override would lose to it. */}
              <div className="w-32 flex-shrink-0">
                <Select
                  uiSize="sm"
                  aria-label={t('works:filters.year')}
                  value={archiveYear}
                  onChange={(e) => setArchiveYear(e.target.value)}
                >
                  <option value="">{t('works:filters.year')}…</option>
                  {(facets.data?.years ?? []).map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </Select>
              </div>
              <Button size="sm" variant="secondary" onClick={doArchive} disabled={!archiveYear || archiveMut.isPending}>
                {t('works:actions.archiveYear')}
              </Button>
            </div>
          )}
        </FilterPanel>
      )}

      {/* Table */}
      <Card className="flex flex-1 min-h-0 flex-col">
        <div className="flex-1 min-h-0 overflow-auto px-2 pb-2">
          {query.isLoading ? (
            <div className="p-8 text-center text-sm text-slate-500">{t('works:loading')}</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">{t('works:empty')}</div>
          ) : (
            <table className="table-sticky w-full border-collapse text-sm">
              <thead className="bg-slate-50/95 text-left text-xs uppercase text-slate-500 backdrop-blur-sm dark:bg-slate-800/95 dark:text-slate-400">
                <tr>
                  <SortableHeader<WorkSortField> field="year" current={sort} order={order} onSort={onSort} defaultOrder="desc">
                    {t('works:table.year')}
                  </SortableHeader>
                  <th className="px-3 py-2 text-xs font-semibold uppercase">{t('works:table.period')}</th>
                  <SortableHeader<WorkSortField> field="planFapt" current={sort} order={order} onSort={onSort}>
                    {t('works:table.planFapt')}
                  </SortableHeader>
                  <SortableHeader<WorkSortField> field="primarieComposite" current={sort} order={order} onSort={onSort}>
                    {t('works:table.primarie')}
                  </SortableHeader>
                  <th className="px-3 py-2 text-xs font-semibold uppercase">{t('works:table.parcela')}</th>
                  <SortableHeader<WorkSortField> field="ocolSilvic" current={sort} order={order} onSort={onSort}>
                    {t('works:table.ocol')}
                  </SortableHeader>
                  <SortableHeader<WorkSortField> field="workType" current={sort} order={order} onSort={onSort}>
                    {t('works:table.workType')}
                  </SortableHeader>
                  <SortableHeader<WorkSortField> field="wages" current={sort} order={order} onSort={onSort} defaultOrder="desc" align="right">
                    {t('works:table.wages')}
                  </SortableHeader>
                  <SortableHeader<WorkSortField> field="totalFinal" current={sort} order={order} onSort={onSort} defaultOrder="desc" align="right">
                    {t('works:table.total')}
                  </SortableHeader>
                  {/* Source document from the Drive workbook's PDF_link
                      column. SUPER_ADMIN only for now — the links point at
                      an external archive nobody else has been granted. */}
                  {isSuperAdmin ? (
                    <th className="px-3 py-2 text-center text-xs font-semibold uppercase">
                      {t('works:table.document')}
                    </th>
                  ) : null}
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase">{t('works:table.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((w) => (
                  <tr key={w.id} className="border-t border-slate-100 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800/60">
                    <td className="px-3 py-1.5">
                      {w.year}
                      {w.isArchived && <span className="ml-1 rounded bg-slate-200 px-1 text-[10px] text-slate-600">arh</span>}
                    </td>
                    <td className="px-3 py-1.5 text-slate-500">
                      {[w.month && `L${w.month}`, w.quarter && `T${w.quarter}`].filter(Boolean).join(' · ')}
                    </td>
                    <td className="px-3 py-1.5">
                      <span className={w.planFapt === 'FAPT' ? 'font-medium text-emerald-600' : 'text-slate-600'}>
                        {t(`works:planFapt.${w.planFapt}`)}
                      </span>
                    </td>
                    <td className="px-3 py-1.5">{w.primarieComposite ?? '—'}</td>
                    <td className="px-3 py-1.5">{w.parcelaPlus ?? w.parcela ?? '—'}</td>
                    <td className="px-3 py-1.5">{w.ocolSilvic ?? '—'}</td>
                    <td className="max-w-[280px] truncate px-3 py-1.5" title={w.workType ?? ''}>{w.workType ?? '—'}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{w.wages !== null ? lei.format(w.wages) : '—'}</td>
                    <td className="px-3 py-1.5 text-right font-medium tabular-nums">{w.totalFinal !== null ? lei.format(w.totalFinal) : '—'}</td>
                    {isSuperAdmin ? (
                      <td className="px-3 py-1.5 text-center">
                        {safeUrl(w.pdfLink ?? '') ? (
                          <a
                            href={safeUrl(w.pdfLink ?? '')}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={w.pdfLink ?? ''}
                            aria-label={t('works:table.document')}
                            className="inline-flex rounded p-1 text-slate-500 transition-colors hover:bg-forest-50 hover:text-forest-700 dark:hover:bg-forest-900/20"
                          >
                            <FileText className="h-4 w-4" />
                          </a>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                    ) : null}
                    <td className="px-3 py-1.5 text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => openEdit(w)} title={t('works:rowActions.edit')} className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-forest-600 dark:hover:bg-slate-700">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button onClick={() => doDelete(w)} title={t('works:rowActions.delete')} className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {/* Outside the scroll container: the pager and the rows-per-page
            select stay pinned to the bottom of the card. */}
        <TablePagination
          className="border-t border-slate-200 px-2 py-1.5 dark:border-slate-700"
          page={page}
          limit={limit}
          total={pagination?.total ?? 0}
          totalPages={pagination?.totalPages ?? 1}
          onPageChange={setPage}
          onLimitChange={(next) => { setPage(1); setLimit(next); }}
          rowsLabel={t('common:common.rows')}
          prevLabel={t('common:pagination.prev')}
          nextLabel={t('common:pagination.next')}
        />
      </Card>

      <WorkFormDialog open={formOpen} onClose={() => setFormOpen(false)} work={editing} />
    </div>
  );
}

